import React, { useState, useEffect } from "react";
import Auth from "./Auth";
import { CiViewList } from "react-icons/ci";
import { motion, AnimatePresence } from "framer-motion";
import {
  slideFromTop,
} from "../../animations/Animations";
import { Link, NavLink, useLocation } from "react-router-dom";

const Login: React.FC = () => {
  const location = useLocation();
  const isSignUp = location.pathname === "/signup";
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  const features = [
    "Streamline your visits and maximize your impact.",
    "Create managers to manage your scheduling activities",
    "Create sales rep to enhance your business capabilities",
    "Your gateway to effective scheduling and client management is here.",
    "Efficiently plan your visitation routes and ensure a productive day in the field.",
    "Access your personalized dashboard to schedule appointments, track visits, and improve your engagement with clients.",
  ];

  // Check if the screen is mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);

    return () => {
      window.removeEventListener("resize", checkIfMobile);
    };
  }, []);

  // Auto-advancing slideshow
  useEffect(() => {
    if (!isMobile) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) =>
          prev === features.length - 1 ? 0 : prev + 1
        );
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [isMobile, features.length]);

  return (
    <div className="flex items-center justify-center p-4 min-h-screen bg-gray-50">
      <div className="max-w-7xl w-full bg-white shadow-lg rounded-xl p-8 flex flex-col lg:flex-row">
        {/* Left side - Features section (hidden on mobile) */}
        <div
          className={`${isMobile ? "hidden" : "block"
            } lg:w-1/2 lg:pr-8 mb-8 lg:mb-0`}
        >
          <motion.h1
            initial="hidden"
            animate="visible"
            variants={slideFromTop}
            className="text-3xl lg:text-4xl font-bold mb-6 text-center lg:text-left bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent"
          >
            Welcome to Syncai
          </motion.h1>

          {/* Enhanced carousel with fade animations but original font size */}
          <div className="relative h-64 flex items-center justify-center rounded-xl p-6 ">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.7 }}
                className="text-center px-6"
              >
                <CiViewList className="w-6 h-6 mx-auto mb-4 text-blue-500" />
                <p className="text-gray-600">{features[currentSlide]}</p>
              </motion.div>
            </AnimatePresence>

            {/* Improved carousel indicators */}
            <div className="absolute -bottom-4 left-0 right-0 flex justify-center space-x-3 pb-2">
              {features.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${currentSlide === index
                      ? "bg-blue-500 w-4 shadow-md"
                      : "bg-gray-300 hover:bg-blue-300"
                    }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Updated Demo button - no longer full width */}
          <div className="flex justify-left mt-6">
            <NavLink
              to="/pricing"
              className={({ isActive }) =>
                isActive
                  ? "bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium shadow-md transition-all duration-300 flex items-center"
                  : "bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white px-6 py-2 rounded-lg font-medium shadow-md transition-all duration-300 flex items-center"
              }
            >
              <span>View Pricing</span>
            </NavLink>
          </div>
        </div>

        {/* Right side - Auth form */}
        <div
          className={`${isMobile ? "w-full" : "lg:w-1/2 lg:pl-8 lg:border-l border-gray-200"
            }`}
        >
          {/* For mobile: simplified welcome message */}
          {isMobile && (
            <motion.h1
              initial="hidden"
              animate="visible"
              variants={slideFromTop}
              className="text-3xl font-bold mb-6 text-center bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent"
            >
              Welcome to Syncai
            </motion.h1>
          )}

          <h2 className="text-3xl lg:text-4xl font-semibold mb-6 text-gray-800">
            {isSignUp ? "Create your account" : "Login to your account"}
          </h2>
          <p className="mb-8 text-gray-700">
            {isSignUp ? "Already have an account?" : "Don't have an account?"}{" "}
            <Link
              to={isSignUp ? "/login" : "/signup"}
              className="text-blue-500 hover:underline"
            >
              {isSignUp ? "Login" : "Create an account"}
            </Link>
          </p>
          <Auth />

          {/* Updated mobile demo button with enhanced styling */}
          {isMobile && (
            <div className="flex justify-center mt-6">
              <NavLink
                to="/pricing"
                className={({ isActive }) =>
                  isActive
                    ? "bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium shadow-md transition-all duration-300 flex items-center"
                    : "bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white px-6 py-2 rounded-lg font-medium shadow-md transition-all duration-300 flex items-center"
                }
              >
                <span>View Pricing</span>
              </NavLink>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
